/* eslint-disable max-lines */
import { v4 as uuidv4 } from 'uuid';
import { Logger } from '@elliemae/pui-diagnostics';
import { IScriptingObject, Events } from '@elliemae/pui-scripting-object';
import type {
  EventListeners,
  Listener,
  SubscribeParam,
  UnsubscribeParam,
  DispatchEventParam,
  EventOptions,
  FilterCriteria,
} from '@elliemae/microfe-common';

/**
 * details of an event listener
 */
type EventListenerDetails = {
  /**
   * filter criteria for the event listener
   */
  criteria?: FilterCriteria;
  /**
   * event listener
   */
  callback: Listener<any, any, any, any>;
  /**
   * subscription id
   */
  token: string;
};

/**
 * parameters for emitting an event
 */
export type EmitEventParam = {
  /**
   * name of the event
   */
  eventName: string;
  /**
   * scripting object that owns this event
   */
  scriptingObject: IScriptingObject;
  /**
   * event parameters
   */
  eventParams: Record<string, unknown>;
  /**
   * event listeners
   */
  listeners: EventListenerDetails[];
};

/**
 * Event Manager
 */
export class EventManager<AppEvents extends EventListeners = Events> {
  /**
   * event listeners
   */
  #listeners: Map<string, Array<EventListenerDetails>> = new Map();

  /**
   * cache for compiled regex patterns used in filter criteria
   */
  #regexCache = new WeakMap<object, RegExp | null>();

  /**
   * logger instance
   */
  #logger?: Logger;

  constructor(logger?: Logger) {
    this.#logger = logger;
  }

  /**
   * call an async function with a timeout
   * @param promise async function to call
   * @param feedbackWaitTime timeout in milliseconds
   * @returns result of the async function or undefined if timeout
   */
  #asyncCallwithTimeout = (
    promise: Promise<unknown>,
    feedbackWaitTime: number,
  ) => {
    const timeout = new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, feedbackWaitTime);
    });
    return Promise.race([promise, timeout]);
  };

  /**
   * emit an event to set of listeners
   * @param {EmitEventParam} param - parameters for emitting an event
   */
  #emitEvent = (param: EmitEventParam) => {
    const { eventName, scriptingObject, eventParams, listeners } = param;
    listeners.forEach((listener) => {
      // check for criteria match
      if (listener.criteria) {
        const matches = eventParams
          ? this.#matchesCriteria(eventParams, listener.criteria)
          : false;
        if (!matches) {
          return;
        }
      }
      Promise.resolve(
        listener.callback({
          obj: scriptingObject,
          eventName,
          eventParams,
        }),
      ).catch((e) => {
        this.#logger?.warn(
          `Error in event listener for "${eventName}": ${(e as Error).message}`,
        );
      });
    });
  };

  #emitEventWithFeedback = async ({
    eventId,
    eventName,
    scriptingObject,
    eventParams,
    feedbackWaitTime,
  }: {
    eventId: string;
    eventName: string;
    scriptingObject: IScriptingObject;
    eventParams: Record<string, unknown>;
    feedbackWaitTime: number;
  }) => {
    const listeners = this.#listeners.get(eventId) || [];
    const promises = listeners.map((listener) => {
      // check for criteria match
      if (listener.criteria) {
        const matches = eventParams
          ? this.#matchesCriteria(eventParams, listener.criteria)
          : false;
        if (!matches) {
          return Promise.resolve();
        }
      }
      return this.#asyncCallwithTimeout(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        listener.callback({
          obj: scriptingObject,
          eventName,
          eventParams,
        }),
        feedbackWaitTime,
      );
    });

    const retValues = await Promise.all(promises);
    return retValues.flat(Infinity).filter((v) => v !== undefined);
  };

  /**
   * Checks if a given payload matches all specified filter criteria.
   *
   * Iterates over each entry in the criteria object and evaluates the corresponding condition
   * against the payload's value for that key. Supported condition types include:
   * - `eq`: Checks for strict equality.
   * - `in`: Checks if the value is included in a provided array.
   * - `includes`: Checks if the payload's value (an array) includes a specified element.
   * - `gt`: Checks if the value is greater than a specified number.
   * - `lt`: Checks if the value is less than a specified number.
   * - `regex`: Checks if the value (a string) matches a provided regular expression.
   * @param {Record<string, any>} payload - The object containing values to be checked against the criteria.
   * @param {FilterCriteria} criteria - An object specifying filter conditions for each key.
   * @returns {boolean} `true` if all criteria are satisfied by the payload, otherwise `false`.
   */
  #matchesCriteria(
    payload: Record<string, any>,
    criteria: FilterCriteria,
  ): boolean {
    // eslint-disable-next-line complexity
    return Object.entries(criteria).every(([key, condition]) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const value = payload[key];
      if ('eq' in condition) return value === condition.eq;
      if ('in' in condition)
        return Array.isArray(condition.in) && condition.in.includes(value);
      if ('includes' in condition)
        return Array.isArray(value) && value.includes(condition.includes);
      if ('gt' in condition) {
        const numValue = typeof value === 'number' ? value : Number(value);
        return !Number.isNaN(numValue) && numValue > condition.gt;
      }
      if ('lt' in condition) {
        const numValue = typeof value === 'number' ? value : Number(value);
        return !Number.isNaN(numValue) && numValue < condition.lt;
      }
      if ('regex' in condition) {
        let regex = this.#regexCache.get(condition);
        if (regex === undefined) {
          try {
            regex = new RegExp(condition.regex);
          } catch {
            regex = null;
          }
          this.#regexCache.set(condition, regex);
        }
        return regex !== null && typeof value === 'string' && regex.test(value);
      }

      return false;
    });
  }

  /**
   * dispatch an event
   * @param scriptingObject
   * @param {DispatchEventParam<EventId, Params>} param - parameters for dispatching an event
   */
  dispatchEvent = async <
    EventId extends Extract<keyof AppEvents, string>,
    Params extends Parameters<AppEvents[EventId]>[0]['eventParams'],
    Options extends EventOptions,
  >(
    scriptingObject: IScriptingObject,
    param: DispatchEventParam<EventId, Params, Options>,
  ) => {
    const {
      event: { id, name },
      eventParams,
      eventOptions,
    } = param;
    const { timeout } = eventOptions ?? {};
    if (!id) throw new Error('Event Id is required');
    const listeners = this.#listeners.get(id) || [];
    if (!timeout || timeout <= 0) {
      this.#emitEvent({
        eventName: name,
        scriptingObject,
        eventParams,
        listeners,
      });
      return Promise.resolve();
    }
    return this.#emitEventWithFeedback({
      eventId: id,
      eventName: name,
      scriptingObject,
      eventParams,
      feedbackWaitTime: timeout,
    });
  };

  /**
   * Subscribe to an event
   * @param {SubscribeParam<EventId, AppEvents[EventId]>} param - parameters for subscribing to an event
   */
  subscribe = <EventId extends Extract<keyof AppEvents, string>>(
    param: SubscribeParam<EventId, AppEvents[EventId]>,
  ) => {
    const { eventId, callback, criteria } = param;
    if (!eventId) throw new Error('eventId is required');
    if (!callback) throw new Error('Callback is required');
    const listeners = this.#listeners.get(eventId) || [];
    const token = uuidv4();
    listeners.push({
      token,
      criteria,
      callback,
    });
    this.#listeners.set(eventId, listeners);
    return token;
  };

  /**
   * Unsubscribe from an event
   * @param {UnsubscribeParam<EventId>} param - parameters for unsubscribing from an event
   */
  unsubscribe = <EventId extends Extract<keyof AppEvents, string>>(
    param: UnsubscribeParam<EventId>,
  ) => {
    const { eventId, token } = param;
    if (!eventId) throw new Error('eventId id is required');
    if (token) {
      const listeners = this.#listeners.get(eventId) || [];
      const index = listeners.findIndex((listener) => listener.token === token);
      if (index > -1) {
        listeners.splice(index, 1);
        this.#listeners.set(eventId, listeners);
      }
    } else {
      this.#listeners.delete(eventId);
    }
  };

  /**
   * Unsubscribe from all events
   */
  unsubscribeAll = () => {
    this.#listeners.clear();
  };
}
